import React, { useState } from "react";

import t from "../common/localization";

import {
  Button,
  Checkbox,
  FilledInput,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  FormLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import AddAttributeDialog from "./AddAttributeDialog";
import TextField from "@material-ui/core/TextField";
import TimezoneSelect from "react-timezone-select";

const useStyles = makeStyles((theme) => ({
  addButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  removeButton: {
    marginRight: theme.spacing(1.5),
  },
  textField: {
    margin: "4px",
    width: "85%",
  },
  timezoneSelector: {
    margin: "4px",
    width: "75%",
  },
}));

const EditAttributesView = ({ attributes, setAttributes, definitions }) => {
  const classes = useStyles();

  const timeZones = {
    "Pacific/Midway": "Midway Island, Samoa",
    "Pacific/Honolulu": "Hawaii",
    "America/Juneau": "Alaska",
    "America/Boise": "Mountain Time",
    "America/Chihuahua": "Chihuahua, La Paz, Mazatlan",
    "America/Phoenix": "Arizona",
    "America/Chicago": "Central Time",
    "America/Regina": "Saskatchewan",
    "America/Mexico_City": "Guadalajara, Mexico City, Monterrey",
    "America/Belize": "Central America",
    "America/Detroit": "Eastern Time",
    "America/Bogota": "Bogota, Lima, Quito",
    "America/Caracas": "Caracas, La Paz",
    "America/Santiago": "Santiago",
    "America/St_Johns": "Newfoundland and Labrador",
    "America/Sao_Paulo": "Brasilia",
    "America/Tijuana": "Tijuana, Pacific Time",
    "America/Argentina/Buenos_Aires": "Buenos Aires, Georgetown",
    "America/Godthab": "Greenland",
    "Atlantic/Azores": "Azores",
    "Atlantic/Cape_Verde": "Cape Verde Islands",
    GMT: "Dublin, Edinburgh, Lisbon, London",
    "Africa/Casablanca": "Casablanca, Monrovia",
    "Atlantic/Canary": "Canary Islands",
    "Europe/Belgrade": "Belgrade, Bratislava, Budapest, Ljubljana, Prague",
    "Europe/Sarajevo": "Sarajevo, Skopje, Warsaw, Zagreb",
    "Europe/Brussels": "Brussels, Copenhagen, Madrid, Paris",
    "Europe/Amsterdam": "Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
    "Africa/Algiers": "West Central Africa",
    "Europe/Bucharest": "Bucharest",
    "Africa/Cairo": "Cairo",
    "Europe/Helsinki": "Helsinki, Kiev, Riga, Sofia, Tallinn, Vilnius",
    "Europe/Athens": "Athens, Istanbul, Minsk",
    "Asia/Jerusalem": "Jerusalem",
    "Africa/Harare": "Harare, Pretoria",
    "Europe/Moscow": "Moscow, St. Petersburg, Volgograd",
    "Asia/Kuwait": "Kuwait, Riyadh",
    "Africa/Nairobi": "Nairobi",
    "Asia/Baghdad": "Baghdad",
    "Asia/Tehran": "Tehran",
    "Asia/Dubai": "Abu Dhabi, Muscat",
    "Asia/Baku": "Baku, Tbilisi, Yerevan",
    "Asia/Kabul": "Kabul",
    "Asia/Yekaterinburg": "Ekaterinburg",
    "Asia/Karachi": "Islamabad, Karachi, Tashkent",
    "Asia/Kolkata": "Chennai, Kolkata, Mumbai, New Delhi",
    "Asia/Kathmandu": "Kathmandu",
    "Asia/Dhaka": "Astana, Dhaka",
    "Asia/Colombo": "Sri Jayawardenepura",
    "Asia/Almaty": "Almaty, Novosibirsk",
    "Asia/Rangoon": "Yangon Rangoon",
    "Asia/Bangkok": "Bangkok, Hanoi, Jakarta",
    "Asia/Krasnoyarsk": "Krasnoyarsk",
    "Asia/Shanghai": "Beijing, Chongqing, Hong Kong SAR, Urumqi",
    "Asia/Kuala_Lumpur": "Kuala Lumpur, Singapore",
    "Asia/Taipei": "Taipei",
    "Australia/Perth": "Perth",
    "Asia/Irkutsk": "Irkutsk, Ulaanbaatar",
    "Asia/Seoul": "Seoul",
    "Asia/Tokyo": "Osaka, Sapporo, Tokyo",
    "Asia/Yakutsk": "Yakutsk",
    "Australia/Darwin": "Darwin",
    "Australia/Adelaide": "Adelaide",
    "Australia/Sydney": "Canberra, Melbourne, Sydney",
    "Australia/Brisbane": "Brisbane",
    "Australia/Hobart": "Hobart",
    "Asia/Vladivostok": "Vladivostok",
    "Pacific/Guam": "Guam, Port Moresby",
    "Asia/Magadan": "Magadan, Solomon Islands, New Caledonia",
    "Pacific/Fiji": "Fiji Islands, Kamchatka, Marshall Islands",
    "Pacific/Auckland": "Auckland, Wellington",
    "Pacific/Tongatapu": "Nuku'alofa",
  };
  const [addDialogShown, setAddDialogShown] = useState(false);

  const convertToList = (attributes) => {
    let booleanList = [];
    let otherList = [];
    for (const key in attributes) {
      const value = attributes[key];
      const type = getAttributeType(value);
      if (type === "boolean") {
        booleanList.push({ key, value, type });
      } else {
        otherList.push({ key, value, type });
      }
    }
    return otherList.concat(booleanList);
  };

  const handleAddResult = (definition) => {
    setAddDialogShown(false);
    if (definition) {
      switch (definition.type) {
        case "number":
          updateAttribute(definition.key, 0);
          break;
        case "boolean":
          updateAttribute(definition.key, false);
          break;
        case "timezone":
          updateAttribute(definition.key, "Asia/Kolkata");
          break;
        default:
          updateAttribute(definition.key, "");
          break;
      }
    }
  };

  const updateAttribute = (key, value) => {
    let updatedAttributes = { ...attributes };
    updatedAttributes[key] = value;
    setAttributes(updatedAttributes);
  };

  const deleteAttribute = (key) => {
    let updatedAttributes = { ...attributes };
    delete updatedAttributes[key];
    setAttributes(updatedAttributes);
  };

  const getAttributeName = (key) => {
    const definition = definitions[key];
    return definition ? definition.name : key;
  };

  const getAttributeType = (value) => {
    if (typeof value === "number") {
      return "number";
    } else if (typeof value === "boolean") {
      return "boolean";
    } else if (timeZones[value]) {
      return "timezone";
    } else {
      return "string";
    }
  };

  const toSkipAttributes = [];
  toSkipAttributes.push("overstayEventAt");
  toSkipAttributes.push("idleEventAt");
  toSkipAttributes.push("documentExpiryDetails");
  toSkipAttributes.push("fuelReadings");
  toSkipAttributes.push("commandPermission");
  toSkipAttributes.push("fcm.token");
  toSkipAttributes.push("fcmDetails");
  toSkipAttributes.push("documentExpiry");
  toSkipAttributes.push("devHisDays");
  toSkipAttributes.push("parkingMode");
  toSkipAttributes.push("preExpiry");

  return (
    <>
      <Grid container direction="row">
        {convertToList(attributes).map(({ key, value, type }) => {
          if (type === "object") {
            return;
          } else if (toSkipAttributes.some((attribute) => attribute === key)) {
            return;
          }
          if (type === "boolean") {
            return (
              <Grid container direction="row" justify="space-between">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      onChange={(e) => updateAttribute(key, e.target.checked)}
                    />
                  }
                  label={getAttributeName(key)}
                />
                <IconButton
                  color="primary"
                  className={classes.removeButton}
                  onClick={() => deleteAttribute(key)}
                >
                  <CloseIcon color="primary" />
                </IconButton>
              </Grid>
            );
          } else if (type === "timezone") {
            return (
              <Grid container direction="row" justify="space-between">
                <Grid item xs={12}>
                  <FormLabel style={{ fontSize: "14px" }}>
                    {getAttributeName(key)}
                  </FormLabel>
                </Grid>
                {/* <FormLabel>{getAttributeName(key)}</FormLabel> */}
                <TimezoneSelect
                  className={classes.timezoneSelector}
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 5,
                    borderColor: "green",
                  })}
                  value={value}
                  onChange={(setSelectedTimezone) =>
                    updateAttribute(key, setSelectedTimezone.value)
                  }
                />
                <IconButton
                  color="primary"
                  className={classes.removeButton}
                  onClick={() => deleteAttribute(key)}
                >
                  <CloseIcon color="primary" />
                </IconButton>
              </Grid>
            );
          } else {
            return (
              <Grid container direction="row" justify="space-between">
                <FormControl variant="filled" margin="normal" key={key}>
                  <TextField
                    type={type === "number" ? "number" : "text"}
                    className={classes.textField}
                    margin="normal"
                    value={value || ""}
                    onChange={(e) => {
                      let value =
                        type === "number"
                          ? Number(e.target.value)
                          : e.target.value;
                      updateAttribute(key, value);
                    }}
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            color="primary"
                            onClick={() => deleteAttribute(key)}
                          >
                            <CloseIcon color="primary" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    label={getAttributeName(key)}
                    variant="standard"
                  />
                </FormControl>
              </Grid>
            );
          }
        })}
        <Button
          size="medium"
          variant="contained"
          color="primary"
          onClick={() => setAddDialogShown(true)}
          startIcon={<AddIcon />}
          className={classes.addButton}
        >
          {t("sharedAdd")}
        </Button>
        <AddAttributeDialog
          open={addDialogShown}
          onResult={handleAddResult}
          definitions={definitions}
        />
      </Grid>
    </>
  );
};

export default EditAttributesView;
